import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Button,
  Paper,
  Divider,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
} from '@mui/material';
import {
  AccountCircleOutlined,
  SecurityOutlined,
  SaveOutlined,
  BusinessOutlined,
  PhoneOutlined,
  HomeOutlined,
  AccountBalanceOutlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import axios from 'axios';

const SECTION_STYLE = { p: 3, mb: 3 };
const SAVE_BTN = { bgcolor: '#1a365d', '&:hover': { bgcolor: '#2d3748' } };

const SectionHeader = ({ icon, title }) => (
  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    {React.cloneElement(icon, { sx: { mr: 1 } })}
    {title}
  </Typography>
);

const ProfilBearbeiten = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingStamm, setSavingStamm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form States
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Stammdaten Vermieter
  const [stamm, setStamm] = useState({
    firma: '', telefon: '', strasse: '', hausnummer: '',
    plz: '', ort: '', iban: '', bic: '', bank_name: '',
  });
  const [stammBefuellt, setStammBefuellt] = useState(false);

  // 2FA States
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [twoFactorDialog, setTwoFactorDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const showMsg = useCallback((msg, isError = false) => {
    if (isError) setError(msg);
    else setSuccess(msg);
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      const [profileRes, stammRes] = await Promise.all([
        axios.get('/api/user/profile'),
        axios.get('/api/user/stammdaten'),
      ]);
      const p = profileRes.data;
      setName(p.username || '');
      setEmail(p.email || '');
      setTwoFactorEnabled(p.two_factor_enabled || false);
      const s = stammRes.data;
      setStamm({
        firma: s.firma || '', telefon: s.telefon || '',
        strasse: s.strasse || '', hausnummer: s.hausnummer || '',
        plz: s.plz || '', ort: s.ort || '',
        iban: s.iban || '', bic: s.bic || '', bank_name: s.bank_name || '',
      });
      setStammBefuellt(!!(s.strasse && s.ort && s.iban));
      setLoading(false);
    } catch (err) {
      showMsg('Fehler beim Laden der Benutzerdaten', true);
      setLoading(false);
    }
  }, [showMsg]);

  useEffect(() => { fetchUserData(); }, [fetchUserData]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setSaving(true);
    try {
      await axios.put('/api/user/profile', { name, email });
      showMsg('Profil erfolgreich aktualisiert');
      fetchUserData();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Fehler beim Aktualisieren des Profils', true);
    } finally {
      setSaving(false);
    }
  };

  const handleStammSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setSavingStamm(true);
    try {
      await axios.put('/api/user/stammdaten', stamm);
      showMsg('Stammdaten erfolgreich gespeichert');
      fetchUserData();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Fehler beim Speichern der Stammdaten', true);
    } finally {
      setSavingStamm(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (newPassword !== confirmPassword) {
      return showMsg('Passwörter stimmen nicht überein', true);
    }

    if (newPassword.length < 6) {
      setError('Neues Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setSaving(true);
    try {
      await axios.post('/api/user/change-password', { currentPassword, newPassword });
      showMsg('Passwort erfolgreich geändert');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      showMsg(err.response?.data?.message || 'Fehler beim Ändern des Passworts', true);
    } finally {
      setSaving(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      const response = await axios.post('/api/auth/2fa/enable');
      setQrCode(response.data.qrCode);
      setTwoFactorDialog(true);
    } catch {
      showMsg('Fehler beim Aktivieren der 2FA', true);
    }
  };

  const handleVerify2FA = async () => {
    try {
      await axios.post('/api/auth/2fa/verify', { token: verificationCode });
      showMsg('Zwei-Faktor-Authentifizierung erfolgreich aktiviert');
      setTwoFactorEnabled(true);
      setTwoFactorDialog(false);
      setVerificationCode('');
    } catch {
      showMsg('Ungültiger Verifizierungscode', true);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Möchten Sie die Zwei-Faktor-Authentifizierung wirklich deaktivieren?')) return;
    try {
      await axios.post('/api/auth/2fa/disable');
      showMsg('Zwei-Faktor-Authentifizierung deaktiviert');
      setTwoFactorEnabled(false);
    } catch {
      showMsg('Fehler beim Deaktivieren der 2FA', true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Profil bearbeiten
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ── Profilbild & Basis ──────────────────────── */}
      <Paper elevation={2} sx={SECTION_STYLE}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ width: 80, height: 80, bgcolor: '#1a365d', fontSize: '2rem', mr: 3 }}>
            {name ? name.charAt(0).toUpperCase() : 'U'}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{name}</Typography>
            <Typography variant="body2" color="text.secondary">{email}</Typography>
            {stammBefuellt && (
              <Chip icon={<CheckCircleOutline />} label="Stammdaten vollständig"
                size="small" color="success" variant="outlined" sx={{ mt: 1 }} />
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleProfileUpdate}>
          <SectionHeader icon={<AccountCircleOutlined />} title="Persönliche Informationen" />
          <TextField fullWidth label="Benutzername" value={name}
            onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} required />
          <TextField fullWidth label="E-Mail" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} sx={{ mb: 3 }} required />
          <Button type="submit" variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveOutlined />}
            disabled={saving} sx={SAVE_BTN}>
            Profil speichern
          </Button>
        </form>
      </Paper>

      {/* ── Vermieter-Stammdaten ────────────────────── */}
      <Paper elevation={2} sx={SECTION_STYLE}>
        <SectionHeader icon={<BusinessOutlined />} title="Vermieter-Stammdaten" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Diese Daten werden automatisch in Mietbescheinigungen und anderen PDF-Dokumenten verwendet.
        </Typography>
        <form onSubmit={handleStammSave}>
          <Typography variant="subtitle2" color="text.secondary"
            sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Kontakt
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Firma / Name (Vermieter)" value={stamm.firma}
                onChange={(e) => setStamm(s => ({ ...s, firma: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start"><BusinessOutlined fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Telefon" value={stamm.telefon}
                onChange={(e) => setStamm(s => ({ ...s, telefon: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlined fontSize="small" /></InputAdornment> }} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="text.secondary"
            sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <HomeOutlined fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Anschrift
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Straße" value={stamm.strasse}
                onChange={(e) => setStamm(s => ({ ...s, strasse: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Hausnummer" value={stamm.hausnummer}
                onChange={(e) => setStamm(s => ({ ...s, hausnummer: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="PLZ" value={stamm.plz}
                onChange={(e) => setStamm(s => ({ ...s, plz: e.target.value }))}
                inputProps={{ maxLength: 10 }} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Ort" value={stamm.ort}
                onChange={(e) => setStamm(s => ({ ...s, ort: e.target.value }))} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="text.secondary"
            sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <AccountBalanceOutlined fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Bankverbindung
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="IBAN" value={stamm.iban}
                onChange={(e) => setStamm(s => ({ ...s, iban: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 34 }}
                helperText="z. B. DE89 3704 0044 0532 0130 00" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="BIC" value={stamm.bic}
                onChange={(e) => setStamm(s => ({ ...s, bic: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 11 }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Bank / Institut" value={stamm.bank_name}
                onChange={(e) => setStamm(s => ({ ...s, bank_name: e.target.value }))} />
            </Grid>
          </Grid>

          <Button type="submit" variant="contained"
            startIcon={savingStamm ? <CircularProgress size={20} /> : <SaveOutlined />}
            disabled={savingStamm} sx={SAVE_BTN}>
            Stammdaten speichern
          </Button>
        </form>
      </Paper>

      {/* ── Passwort ändern ─────────────────────────── */}
      <Paper elevation={2} sx={SECTION_STYLE}>
        <SectionHeader icon={<SecurityOutlined />} title="Passwort ändern" />
        <form onSubmit={handlePasswordChange}>
          <TextField fullWidth label="Aktuelles Passwort" type="password"
            value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }} required />
          <TextField fullWidth label="Neues Passwort" type="password"
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }} required helperText="Mindestens 6 Zeichen" />
          <TextField fullWidth label="Neues Passwort bestätigen" type="password"
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 3 }} required />
          <Button type="submit" variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveOutlined />}
            disabled={saving} sx={SAVE_BTN}>
            Passwort ändern
          </Button>
        </form>
      </Paper>

      {/* ── 2FA ────────────────────────────────────── */}
      <Paper elevation={2} sx={SECTION_STYLE}>
        <SectionHeader icon={<SecurityOutlined />} title="Zwei-Faktor-Authentifizierung (2FA)" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Erhöhen Sie die Sicherheit Ihres Kontos mit einer zusätzlichen Authentifizierungsebene.
        </Typography>
        <Card variant="outlined" sx={{ mb: 2, bgcolor: twoFactorEnabled ? '#f0fdf4' : '#fef2f2' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Status: {twoFactorEnabled ? 'Aktiviert ✓' : 'Deaktiviert'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {twoFactorEnabled ? 'Ihr Konto ist durch 2FA geschützt' : 'Aktivieren Sie 2FA für mehr Sicherheit'}
                </Typography>
              </Box>
              <Button variant={twoFactorEnabled ? 'outlined' : 'contained'}
                color={twoFactorEnabled ? 'error' : 'success'}
                onClick={twoFactorEnabled ? handleDisable2FA : handleEnable2FA}>
                {twoFactorEnabled ? 'Deaktivieren' : 'Aktivieren'}
              </Button>
            </Box>
          </CardContent>
        </Card>
        {twoFactorEnabled && (
          <Alert severity="info">
            Bei jedem Login wird ein zusätzlicher Code von Ihrer Authenticator-App abgefragt.
          </Alert>
        )}
      </Paper>

      {/* 2FA Setup Dialog */}
      <Dialog open={twoFactorDialog} onClose={() => setTwoFactorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Zwei-Faktor-Authentifizierung einrichten</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            1. Scannen Sie diesen QR-Code mit Ihrer Authenticator-App (z.B. Google Authenticator, Authy)
          </Typography>
          {qrCode && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <img src={qrCode} alt="2FA QR Code" style={{ maxWidth: '250px' }} />
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            2. Geben Sie den 6-stelligen Code aus Ihrer App ein:
          </Typography>
          <TextField fullWidth label="Verifizierungscode" value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="123456" inputProps={{ maxLength: 6 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFactorDialog(false)}>Abbrechen</Button>
          <Button onClick={handleVerify2FA} variant="contained"
            disabled={verificationCode.length !== 6} sx={SAVE_BTN}>
            Verifizieren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilBearbeiten;

